import { useScenarioForm } from './contexts';
import { DropdownField, NumberField } from 'components/form-fields-2';
import { Button, Checkbox } from '@carbon/react';
import { Add, TrashCan } from '@carbon/react/icons';
import { capitalize } from 'lodash-es';
import {
  GeneralValidationRule,
  MainStream,
  StreamThreshold,
  ValidationType,
  ValidationConfigResponse,
  ValidationConfigsResponse,
  WellSetDefinition,
} from './types';
import { defaultGeneralRule, defaultStreamThreshold } from './default-values';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import sectionStyles from './config-sections.module.scss';
import styles from './validation-configs.module.scss';
import { useEffect, useMemo } from 'react';
import { ValidationConfigsSkeleton } from './section-skeletons';
import { useConfigSwitch } from './hooks';
import { atom, useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';

const FALLBACK_ATOM = atom(null);

// General validation types (non-probability)
const GENERAL_VALIDATION_TYPES: ValidationType[] = [
  'time_since_last_verification',
  'new_wells',
  'new_wells_not_landed',
  'relative_sample_size',
  'stimulation_design_change',
  'well_spacing_design_change',
  'landing_in_zone_change',
];

const STREAM_VALIDATION_TYPES: Array<
  'upper_probability_threshold' | 'lower_probability_threshold'
> = ['upper_probability_threshold', 'lower_probability_threshold'];

const STREAM_OPTIONS: MainStream[] = ['oil', 'gas', 'water'];

const WELL_SET_DEFINITIONS: WellSetDefinition[] = [
  'analog_wells',
  'analog_and_new_wells',
  'same_formation_wells_in_type_curve_area',
];

const VALIDATION_TYPE_LABELS: Record<ValidationType, string> = {
  time_since_last_verification: 'Time Since Last Verification',
  new_wells: 'New Wells',
  new_wells_not_landed: 'New Wells Not Landed',
  relative_sample_size: 'Relative Sample Size',
  stimulation_design_change: 'Stimulation Design Change',
  well_spacing_design_change: 'Well Spacing Design Change',
  landing_in_zone_change: 'Landing In Zone Change',
  upper_probability_threshold: 'Upper Prob. Threshold',
  lower_probability_threshold: 'Lower Prob. Threshold',
};

const WELL_SET_LABELS: Record<WellSetDefinition, string> = {
  analog_wells: 'Analog Wells',
  analog_and_new_wells: 'Analog And New Wells',
  same_formation_wells_in_type_curve_area:
    'Same Formation Wells In Type Curve Area',
};

const validationTypeLabel = (value: string | null) => {
  if (!value) return '';
  return (
    VALIDATION_TYPE_LABELS[value as ValidationType] ??
    value
      .split('_')
      .map((word) => capitalize(word))
      .join(' ')
  );
};

const wellSetLabel = (value: string | null) => {
  if (!value) return '';
  return (
    WELL_SET_LABELS[value as WellSetDefinition] ??
    value
      .split('_')
      .map((word) => capitalize(word))
      .join(' ')
  );
};

/**
 * Returns the threshold label based on validation type.
 * @param {ValidationType} validationType - The validation type
 * @return {string} The threshold label
 */
const getThresholdLabel = (validationType: ValidationType): string => {
  switch (validationType) {
    case 'time_since_last_verification':
      return 'Threshold (months)';
    case 'new_wells':
    case 'new_wells_not_landed':
      return 'Threshold (count)';
    default:
      return 'Threshold (%)';
  }
};

const mapResponseToGeneralRule = (
  resp: ValidationConfigResponse
): GeneralValidationRule => ({
  id: String(resp.id),
  overrideVerification: resp.overrideVerification,
  validationType: resp.validationKind as ValidationType,
  threshold: parseFloat(resp.thresholdValue),
});

const mapResponseToStreamThreshold = (
  resp: ValidationConfigResponse
): StreamThreshold => ({
  id: String(resp.id),
  overrideVerification: resp.overrideVerification,
  validationType: resp.validationKind as
    'upper_probability_threshold' | 'lower_probability_threshold',
  stream: resp.stream as MainStream,
  thresholdPercent: parseFloat(resp.thresholdValue),
  wellSetDefinition: (resp.wellsToCalculate as WellSetDefinition) || null,
});

export const ValidationConfigs = () => {
  const { form, selectedAreaConfig, setFieldBaseline, readOnly } =
    useScenarioForm();

  // Fetch existing validation configs from API
  const validationConfigsHref =
    selectedAreaConfig?.resources?.validationConfigs?.href || null;

  const validationConfigsAtom = useMemo(() => {
    if (!validationConfigsHref) return FALLBACK_ATOM;
    return createResourceAtom<ValidationConfigsResponse>(validationConfigsHref);
  }, [validationConfigsHref]);

  const validationConfigsData = useAtomValue(validationConfigsAtom);

  const initializedForConfigRef = useConfigSwitch(selectedAreaConfig);

  // When API data loads, override with fetched values
  useEffect(() => {
    const configId = selectedAreaConfig?.id ?? null;
    if (initializedForConfigRef.current === configId) return;
    if (
      !validationConfigsData?.data ||
      !('results' in validationConfigsData.data)
    )
      return;
    initializedForConfigRef.current = configId;
    const results = validationConfigsData.data.results;

    // Split by stream presence: null stream = general rule, non-null = stream threshold
    const generalRules = results
      .filter((r) => r.stream === null)
      .map(mapResponseToGeneralRule);
    const streamThresholds = results
      .filter((r) => r.stream !== null)
      .map(mapResponseToStreamThreshold);

    const configs = { generalRules, streamThresholds };
    form.setFieldValue('validationConfigs', configs);
    setFieldBaseline('validationConfigs', configs);
  }, [
    validationConfigsData,
    form,
    setFieldBaseline,
    selectedAreaConfig?.id,
    initializedForConfigRef,
  ]);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary className={sectionStyles.accordionSummaryBorder}>
        <h3 className={sectionStyles.sectionTitle}>Validation Configs</h3>
      </AccordionSummary>
      <AccordionDetails>
        <div className={sectionStyles.sectionDetails}>
          <p className={styles.description}>
            Configure validation rules to flag Type Curves for verification.
          </p>

          {validationConfigsData?.loading ? (
            <ValidationConfigsSkeleton />
          ) : (
            <form.Subscribe
              selector={(state) => state.values.validationConfigs}
            >
              {(validationConfigs) => {
                if (!validationConfigs) return null;

                const usedGeneralTypes = new Set(
                  validationConfigs.generalRules.map((r) => r.validationType)
                );
                const allGeneralTypesUsed =
                  validationConfigs.generalRules.length >=
                  GENERAL_VALIDATION_TYPES.length;

                return (
                  <>
                    {/* General Validation Rules */}
                    {validationConfigs.generalRules.map((rule, index) => (
                      <div
                        key={rule.id || `general-${index}`}
                        className={styles.generalRuleRow}
                      >
                        <div className={styles.checkboxCell}>
                          <Checkbox
                            id={`overrideVerification-${index}`}
                            labelText="Override Verification"
                            checked={rule.overrideVerification}
                            disabled={readOnly}
                            onChange={(
                              _: any,
                              { checked }: { checked: boolean }
                            ) => {
                              const current = [
                                ...validationConfigs.generalRules,
                              ];
                              current[index] = {
                                ...current[index],
                                overrideVerification: checked,
                              };
                              form.setFieldValue('validationConfigs', {
                                ...validationConfigs,
                                generalRules: current,
                              });
                            }}
                          />
                        </div>

                        <DropdownField
                          id={`validationType-${index}`}
                          label="Select type"
                          titleText="Validation Type"
                          items={GENERAL_VALIDATION_TYPES.filter(
                            (t) =>
                              !usedGeneralTypes.has(t) ||
                              t === rule.validationType
                          )}
                          itemToString={(item: string | null) =>
                            validationTypeLabel(item)
                          }
                          value={rule.validationType}
                          disabled={readOnly}
                          onChange={(value) => {
                            const current = [...validationConfigs.generalRules];
                            current[index] = {
                              ...current[index],
                              validationType: value as ValidationType,
                            };
                            form.setFieldValue('validationConfigs', {
                              ...validationConfigs,
                              generalRules: current,
                            });
                          }}
                        />

                        <NumberField
                          id={`threshold-${index}`}
                          label={getThresholdLabel(rule.validationType)}
                          value={rule.threshold}
                          disabled={readOnly}
                          decimalScale={0}
                          isAllowed={(v) => Number.isInteger(v)}
                          onChange={(value) => {
                            const current = [...validationConfigs.generalRules];
                            current[index] = {
                              ...current[index],
                              threshold: Number(value) || 0,
                            };
                            form.setFieldValue('validationConfigs', {
                              ...validationConfigs,
                              generalRules: current,
                            });
                          }}
                        />

                        {!readOnly && (
                          <div className={styles.deleteCell}>
                            <Button
                              kind="ghost"
                              size="sm"
                              hasIconOnly
                              renderIcon={TrashCan}
                              iconDescription="Remove"
                              onClick={() => {
                                const current = [
                                  ...validationConfigs.generalRules,
                                ];
                                current.splice(index, 1);
                                form.setFieldValue('validationConfigs', {
                                  ...validationConfigs,
                                  generalRules: current,
                                });
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    {!readOnly && (
                      <Button
                        kind="tertiary"
                        size="md"
                        renderIcon={Add}
                        className={styles.addButton}
                        disabled={allGeneralTypesUsed}
                        onClick={() => {
                          const firstAvailable = GENERAL_VALIDATION_TYPES.find(
                            (t) => !usedGeneralTypes.has(t)
                          );
                          form.setFieldValue('validationConfigs', {
                            ...validationConfigs,
                            generalRules: [
                              ...validationConfigs.generalRules,
                              {
                                ...defaultGeneralRule,
                                validationType:
                                  firstAvailable ??
                                  defaultGeneralRule.validationType,
                              },
                            ],
                          });
                        }}
                      >
                        Add Validation Rule
                      </Button>
                    )}

                    {/* Stream Dependent Probability Thresholds */}
                    <h4 className={styles.subsectionHeading}>
                      Stream Dependent Probability Thresholds
                    </h4>

                    {validationConfigs.streamThresholds.map(
                      (threshold, index) => (
                        <div
                          key={threshold.id || `stream-${index}`}
                          className={styles.streamThresholdRow}
                        >
                          <div className={styles.checkboxCell}>
                            <Checkbox
                              id={`streamOverride-${index}`}
                              labelText="Override Verification"
                              checked={threshold.overrideVerification}
                              disabled={readOnly}
                              onChange={(
                                _: any,
                                { checked }: { checked: boolean }
                              ) => {
                                const current = [
                                  ...validationConfigs.streamThresholds,
                                ];
                                current[index] = {
                                  ...current[index],
                                  overrideVerification: checked,
                                };
                                form.setFieldValue('validationConfigs', {
                                  ...validationConfigs,
                                  streamThresholds: current,
                                });
                              }}
                            />
                          </div>

                          <DropdownField
                            id={`streamValidationType-${index}`}
                            label="Select type"
                            titleText="Validation Type"
                            items={STREAM_VALIDATION_TYPES}
                            itemToString={(item: string | null) =>
                              validationTypeLabel(item)
                            }
                            value={threshold.validationType}
                            disabled={readOnly}
                            onChange={(value) => {
                              const current = [
                                ...validationConfigs.streamThresholds,
                              ];
                              current[index] = {
                                ...current[index],
                                validationType: value as
                                  | 'upper_probability_threshold'
                                  | 'lower_probability_threshold',
                              };
                              form.setFieldValue('validationConfigs', {
                                ...validationConfigs,
                                streamThresholds: current,
                              });
                            }}
                          />

                          <DropdownField
                            id={`streamOption-${index}`}
                            label="Select stream"
                            titleText="Stream"
                            items={STREAM_OPTIONS}
                            itemToString={(item: string | null) =>
                              capitalize(item ?? '')
                            }
                            value={threshold.stream}
                            disabled={readOnly}
                            onChange={(value) => {
                              const current = [
                                ...validationConfigs.streamThresholds,
                              ];
                              current[index] = {
                                ...current[index],
                                stream: value as MainStream,
                              };
                              form.setFieldValue('validationConfigs', {
                                ...validationConfigs,
                                streamThresholds: current,
                              });
                            }}
                          />

                          <NumberField
                            id={`streamThreshold-${index}`}
                            label="Threshold (%)"
                            value={threshold.thresholdPercent}
                            disabled={readOnly}
                            onChange={(value) => {
                              const current = [
                                ...validationConfigs.streamThresholds,
                              ];
                              current[index] = {
                                ...current[index],
                                thresholdPercent: Number(value) || 0,
                              };
                              form.setFieldValue('validationConfigs', {
                                ...validationConfigs,
                                streamThresholds: current,
                              });
                            }}
                            isAllowed={(v) => v >= 0 && v <= 100}
                          />

                          <DropdownField
                            id={`wellSetDefinition-${index}`}
                            label="Select definition"
                            titleText="Well Set Definition"
                            items={WELL_SET_DEFINITIONS}
                            itemToString={(item: string | null) =>
                              wellSetLabel(item)
                            }
                            value={threshold.wellSetDefinition}
                            disabled={readOnly}
                            onChange={(value) => {
                              const current = [
                                ...validationConfigs.streamThresholds,
                              ];
                              current[index] = {
                                ...current[index],
                                wellSetDefinition:
                                  (value as WellSetDefinition) || null,
                              };
                              form.setFieldValue('validationConfigs', {
                                ...validationConfigs,
                                streamThresholds: current,
                              });
                            }}
                          />

                          {!readOnly && (
                            <div className={styles.deleteCell}>
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                renderIcon={TrashCan}
                                iconDescription="Remove"
                                onClick={() => {
                                  const current = [
                                    ...validationConfigs.streamThresholds,
                                  ];
                                  current.splice(index, 1);
                                  form.setFieldValue('validationConfigs', {
                                    ...validationConfigs,
                                    streamThresholds: current,
                                  });
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )
                    )}

                    {!readOnly && (
                      <Button
                        kind="tertiary"
                        size="md"
                        renderIcon={Add}
                        className={styles.addButton}
                        onClick={() => {
                          form.setFieldValue('validationConfigs', {
                            ...validationConfigs,
                            streamThresholds: [
                              ...validationConfigs.streamThresholds,
                              { ...defaultStreamThreshold },
                            ],
                          });
                        }}
                      >
                        Add Stream Threshold
                      </Button>
                    )}
                  </>
                );
              }}
            </form.Subscribe>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
